import { Injectable } from "@angular/core";
import { BehaviorSubject, map } from "rxjs";

export interface AiStep {
  label: string;
  duration: number; // ms; 0 = hold until complete() is called
  credits?: number;
}

export interface AiTaskConfig {
  title: string;
  steps: AiStep[];
  /** Called (and modal closed) when user clicks "View Results" */
  onViewResults?: () => void;
  /** When true, the minimize button closes the modal instead of collapsing to FAB */
  closeOnMinimize?: boolean;
  /** When true, the minimize button is hidden entirely */
  hideMinimize?: boolean;
}

type TaskState = "idle" | "processing" | "complete";
type ViewState = "hidden" | "modal" | "fab";

@Injectable({ providedIn: "root" })
export class AiProcessingService {
  private _taskState = new BehaviorSubject<TaskState>("idle");
  private _viewState = new BehaviorSubject<ViewState>("hidden");
  private _config = new BehaviorSubject<AiTaskConfig | null>(null);
  private _currentStep = new BehaviorSubject<number>(0);
  private _progress = new BehaviorSubject<number>(0);
  private _elapsed = new BehaviorSubject<number>(0);
  private _credits = new BehaviorSubject<number>(0);

  readonly taskState$ = this._taskState.asObservable();
  readonly config$ = this._config.asObservable();
  readonly currentStep$ = this._currentStep.asObservable();
  readonly progress$ = this._progress.asObservable();
  readonly elapsed$ = this._elapsed.asObservable();
  readonly credits$ = this._credits.asObservable();

  readonly modalVisible$ = this._viewState.pipe(map((v) => v === "modal"));
  readonly fabVisible$ = this._viewState.pipe(map((v) => v === "fab"));

  private stepTimers: ReturnType<typeof setTimeout>[] = [];
  private elapsedInterval: ReturnType<typeof setInterval> | null = null;

  start(config: AiTaskConfig): void {
    this.cleanup();
    this._config.next(config);
    this._taskState.next("processing");
    this._viewState.next("modal");
    this._currentStep.next(0);
    this._progress.next(0);
    this._elapsed.next(0);
    this._credits.next(0);
    this.startElapsedTimer();
    this.scheduleSteps(config.steps);
  }

  complete(): void {
    const steps = this._config.value?.steps ?? [];
    this._currentStep.next(steps.length); // all done
    this._progress.next(100);
    this._taskState.next("complete");
    this.stopElapsedTimer();
    // Tally final credits
    const total = steps.reduce((sum, s) => sum + (s.credits ?? 0), 0);
    this._credits.next(total);
  }

  minimize(): void {
    if (this._taskState.value === "idle") return;
    if (this._config.value?.closeOnMinimize) {
      this.close();
    } else {
      this._viewState.next("fab");
    }
  }

  viewResults(): void {
    const cb = this._config.value?.onViewResults;
    this.close();
    cb?.();
  }

  restore(): void {
    if (this._viewState.value === "fab") {
      this._viewState.next("modal");
    }
  }

  close(): void {
    this.cleanup();
    this._taskState.next("idle");
    this._viewState.next("hidden");
  }

  private scheduleSteps(steps: AiStep[]): void {
    let delay = 0;
    // Advance through all steps except the last (which waits for complete())
    for (let i = 0; i < steps.length - 1; i++) {
      const step = steps[i];
      if (!step.duration) continue;
      delay += step.duration;
      const nextIndex = i + 1;
      const timer = setTimeout(() => {
        this._currentStep.next(nextIndex);
        // Progress: advancing to step N means N/total * 90 (leave 10% for final API response)
        this._progress.next(Math.round((nextIndex / steps.length) * 90));
        // Accumulate credits up to this step
        const earnedCredits = steps
          .slice(0, nextIndex)
          .reduce((sum, s) => sum + (s.credits ?? 0), 0);
        this._credits.next(earnedCredits);
      }, delay);
      this.stepTimers.push(timer);
    }
  }

  private startElapsedTimer(): void {
    this.elapsedInterval = setInterval(() => {
      this._elapsed.next(this._elapsed.value + 1000);
    }, 1000);
  }

  private stopElapsedTimer(): void {
    if (this.elapsedInterval !== null) {
      clearInterval(this.elapsedInterval);
      this.elapsedInterval = null;
    }
  }

  private cleanup(): void {
    this.stepTimers.forEach((t) => clearTimeout(t));
    this.stepTimers = [];
    this.stopElapsedTimer();
  }
}
